"use client";

import { useState } from "react";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { Building2, FlaskConical, Globe, KeySquare, Loader2, ShieldAlert } from "lucide-react";
import { StatusMessage } from "@/components/ui/status-message";
import { useTranslation, type TranslationKey } from "@/lib/i18n";
import { bff, bffErrorBody } from "@/lib/api/bff";
import type { DevAuthMode, DevPersonaRole, DevPersonaView } from "@/lib/auth/dev-login-types";

/**
 * Development-only quick-login panel.
 *
 * Rendered **only** when `app/login/page.tsx` — a Server Component — hands it
 * data, which it does only when `devLoginGate()` opens (see
 * `lib/auth/dev-login.ts` for the three conditions that must all hold). The
 * props carry persona ids, usernames and roles; never a password. Clicking a
 * persona posts its **id** to `/api/auth/dev-login`, which reads the credential
 * from the server environment and performs the sign-in server-side.
 *
 * The visual language is deliberately unlike the rest of the product — dashed
 * warning-tinted border, flask icon, an explicit "development only" badge — so
 * it can never be mistaken for a shipped feature. Colours come from the
 * `warning` theme tokens, so tenant palettes and dark mode both work. Note the
 * token contract `app/globals.css` states for this pair: `--warning` is the FILL
 * and `--warning-foreground` is text on the PAGE background, so the house
 * pairing is `bg-warning/15 text-warning-foreground`. `bg-warning` with
 * `text-warning-foreground` would render light-on-light in dark mode.
 */

interface DevLoginPanelProps {
  personas: readonly DevPersonaView[];
  activeMode: DevAuthMode;
  returnTo: string;
}

/** Literal keys (never template-built) so the i18n audits can see the references. */
const ROLE_LABEL: Record<DevPersonaRole, TranslationKey> = {
  operator: "devLogin.role.operator",
  tenantAdmin: "devLogin.role.tenantAdmin",
  analyst: "devLogin.role.analyst",
};

const ROLE_HINT: Record<DevPersonaRole, TranslationKey> = {
  operator: "devLogin.roleHint.operator",
  tenantAdmin: "devLogin.roleHint.tenantAdmin",
  analyst: "devLogin.roleHint.analyst",
};

const MODE_LABEL: Record<DevAuthMode, TranslationKey> = {
  external: "devLogin.mode.external",
  local: "devLogin.mode.local",
};

const REASON_LABEL: Record<string, TranslationKey> = {
  operatorNeedsExternal: "devLogin.unavailable.operatorNeedsExternal",
  multiNeedsExternal: "devLogin.unavailable.multiNeedsExternal",
  needsExternal: "devLogin.unavailable.needsExternal",
  needsLocal: "devLogin.unavailable.needsLocal",
};

const ERROR_LABEL: Record<string, TranslationKey> = {
  credentials: "devLogin.error.credentials",
  idp: "devLogin.error.idp",
  unavailable: "devLogin.error.unavailable",
};

interface QuickLoginResponse {
  ok?: boolean;
  target?: string;
  error?: string;
  reason?: string;
}

export function DevLoginPanel({ personas, activeMode, returnTo }: DevLoginPanelProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<TranslationKey | null>(null);

  async function signIn(personaId: string): Promise<void> {
    setError(null);
    setPending(personaId);
    try {
      // Every negative outcome arrives as a non-2xx with a `reason` in the body,
      // so a rejection is data rather than an exception — the credential form
      // uses the same `bffErrorBody` unwrap.
      let body: QuickLoginResponse | null;
      try {
        body = await bff.post<QuickLoginResponse>("/api/auth/dev-login", {
          persona: personaId,
          returnTo,
        });
      } catch (err) {
        body = bffErrorBody<QuickLoginResponse>(err);
      }

      if (body?.ok && typeof body.target === "string") {
        // Full navigation via the router so the server components re-read the
        // freshly created session — the same handoff the credential form uses.
        router.push(body.target as Route);
        router.refresh();
        return;
      }
      const key = body?.error === "unavailable" ? "unavailable" : (body?.reason ?? "idp");
      setError(ERROR_LABEL[key] ?? "devLogin.error.idp");
    } catch {
      setError("devLogin.error.idp");
    } finally {
      setPending(null);
    }
  }

  if (personas.length === 0) return null;

  // Available personas first. The server lists Keycloak personas before local
  // ones, so in local-auth mode the only two you can actually click sat *below*
  // four disabled rows with long explanatory text — the useful buttons were the
  // last thing on the page. Order is otherwise preserved (stable sort), so the
  // grouping within each half is unchanged.
  const ordered = [...personas].sort(
    (a, b) => Number(b.available) - Number(a.available),
  );

  return (
    <section
      data-testid="dev-login-panel"
      aria-label={t("devLogin.title")}
      className="rounded-md border-2 border-dashed border-warning/70 bg-warning/10 p-4"
    >
      <div className="mb-3 flex items-start gap-2">
        <FlaskConical className="mt-0.5 size-4 shrink-0 text-warning-foreground" aria-hidden />
        <div className="space-y-1">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-foreground">
            {t("devLogin.title")}
            <span className="rounded-sm border border-warning/40 bg-warning/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-foreground">
              {t("devLogin.badge")}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">{t("devLogin.description")}</p>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Globe className="size-3" aria-hidden />
            <span>{t("devLogin.activeModeLabel")}</span>
            <span className="font-medium text-foreground" data-testid="dev-login-active-mode">
              {t(MODE_LABEL[activeMode])}
            </span>
          </p>
        </div>
      </div>

      {/* E2 (§2.1): the persona buttons are all still on screen and pressing
          another one is exactly what the user does next, so the refusal belongs
          here and has to stay until it is acted on — not in a toast. */}
      <StatusMessage tone="danger" className="mb-3 text-xs" data-testid="dev-login-error">
        {error ? t(error) : null}
      </StatusMessage>

      {/* Two columns from `sm` up: six personas stacked made the dev panel taller
          than the login form it sits under. Each row is self-contained, so pairing
          them costs nothing and halves the page height. */}
      <ul className="grid gap-2 sm:grid-cols-2">
        {ordered.map((persona) => {
          const reasonKey = persona.unavailableReason
            ? (REASON_LABEL[persona.unavailableReason] ?? "devLogin.unavailable.needsExternal")
            : null;
          return (
            <li key={persona.id}>
              <button
                type="button"
                data-testid={`dev-login-${persona.id}`}
                // E0 (§2.2): `aria-disabled`, not `disabled`, while a sign-in is
                // in flight — the pressed button keeps focus and keeps its name.
                // An unavailable persona stays natively `disabled`: that is a
                // permanent state, not a pending one.
                disabled={!persona.available}
                aria-disabled={pending !== null || undefined}
                aria-busy={pending === persona.id || undefined}
                aria-describedby={reasonKey ? `dev-login-reason-${persona.id}` : undefined}
                onClick={() => {
                  if (pending !== null) return;
                  void signIn(persona.id);
                }}
                /* Hover goes to `muted`, NOT `accent`: the children carry their own
                   `text-muted-foreground` / `text-warning-foreground` and those do not
                   change on hover, so an `accent` fill (orange at hue 35 in dark mode)
                   put grey and amber text on orange. `muted` is the neutral surface
                   those foreground tokens are defined against, so every child stays
                   legible without overriding any of them. */
                className="flex w-full items-start gap-2.5 rounded-md border border-border bg-background px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-background aria-disabled:cursor-default aria-disabled:opacity-60 aria-disabled:hover:border-border aria-disabled:hover:bg-background"
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden>
                  {persona.role === "operator" ? (
                    <ShieldAlert className="size-4" />
                  ) : (
                    <Building2 className="size-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  {/* Line 1: who — role, mode, and the address that identifies it. */}
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-semibold text-foreground">
                      {t(ROLE_LABEL[persona.role])}
                    </span>
                    <span className="rounded-sm border border-border px-1 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
                      {t(MODE_LABEL[persona.mode])}
                    </span>
                    <span className="truncate font-mono text-xs text-muted-foreground">
                      {persona.username}
                    </span>
                  </span>
                  {/* Line 2: scope — tenants, then the role hint, on ONE line. Five
                      stacked lines per persona made the page taller than the form it
                      belongs to; the hint stays visible (and fully readable via
                      `title`) rather than being dropped. */}
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                    <KeySquare className="size-3 shrink-0" aria-hidden />
                    {/* The icon carries the meaning visually and the label would
                        cost width on a two-column row — but the icon is
                        aria-hidden, so without this the tenant list reaches a
                        screen reader as a bare value with no idea what it is. */}
                    <span className="sr-only">{t("devLogin.tenantsLabel")}</span>
                    <span className="font-medium text-foreground">
                      {persona.tenants.length > 0
                        ? persona.tenants.join(", ")
                        : t("devLogin.noTenant")}
                    </span>
                    <span aria-hidden>·</span>
                    <span className="min-w-0 truncate" title={t(ROLE_HINT[persona.role])}>
                      {t(ROLE_HINT[persona.role])}
                    </span>
                  </span>
                  {reasonKey ? (
                    <span
                      id={`dev-login-reason-${persona.id}`}
                      className="mt-0.5 block text-xs text-warning-foreground"
                      data-testid={`dev-login-reason-${persona.id}`}
                    >
                      {t(reasonKey)}
                    </span>
                  ) : null}
                </span>
                {pending === persona.id ? (
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin" aria-hidden />
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
